import * as React from "react";
import { useEffect, useCallback } from "react";
import {
  Table,
  SpaceBetween,
  Header,
  Container,
  TableProps,
  Spinner,
} from "@amzn/awsui-components-console";
import {
  Ard,
  UseCase,
  UslLine,
  UseCaseName,
} from "@amzn/fuse-accounting-central-datastore-client";
import { UseCaseTableCell } from "./use-case-table-cell";
import { useFlareContext } from "src/context/flare/flareContext";
import { UslLineDiscrepancies } from "src/context/flare/flare-comparison-service";
import { SEVEN_SEGMENTS_USL_LINE_FIELDS } from "src/context/flare/constants";
import {
  SPLIT_PANEL_STICKY_HEADER_OFFSET,
  ANCHOR_SCROLL_MARGIN_TOP,
  getUseCaseSectionId,
} from "./split-panel.helper";
import { useJemGen } from "src/context/jem-gen/jemGenContext";
import { USL_LINE_KEY_TO_HEADER } from "src/components/constants/appConstants";

interface UseCaseTablesProps {
  displayedArd: Ard | null;
}

export function UseCaseTables({ displayedArd }: UseCaseTablesProps) {
  const { ardFlareComparison, compareArdWithFlare, flareConfig } =
    useFlareContext();
  const { coaDescriptions, isLoadingCoaDescriptions } = useJemGen();

  useEffect(() => {
    compareArdWithFlare(displayedArd);
  }, [displayedArd, flareConfig]);

  // Memoized helper function to get discrepancies for a specific use case and USL line
  const getUslLineDiscrepancies = useCallback(
    (
      useCaseName: string,
      uslLineIndex: number,
    ): UslLineDiscrepancies | undefined => {
      if (!ardFlareComparison) return undefined;

      const useCaseDiscrepancies = ardFlareComparison.useCaseDiscrepancies.find(
        (uc) => uc.useCaseName === useCaseName,
      );

      if (!useCaseDiscrepancies) return undefined;

      return useCaseDiscrepancies.uslLineDiscrepancies[uslLineIndex];
    },
    [ardFlareComparison],
  );

  const createColumnDefinitions = useCallback(
    (
      useCaseName: string,
    ): TableProps.ColumnDefinition<{ uslLine: UslLine; index: number }>[] => {
      type ItemType = { uslLine: UslLine; index: number };

      const accountNameColumn: TableProps.ColumnDefinition<ItemType> = {
        id: "accountName",
        header: "Account Name",
        minWidth: 150,
        cell: (item: ItemType) => {
          const accountNumber = item.uslLine.accountNumber;
          if (isLoadingCoaDescriptions) {
            return <Spinner />;
          }
          if (!accountNumber) {
            return "-";
          }
          return coaDescriptions.get(`accountNumber:${accountNumber}`) || "-";
        },
      };

      // Seven-segment columns with discrepancy support
      type SevenSegmentFieldKey =
        (typeof SEVEN_SEGMENTS_USL_LINE_FIELDS)[keyof typeof SEVEN_SEGMENTS_USL_LINE_FIELDS];
      const sevenSegmentFields: {
        fieldKey: SevenSegmentFieldKey;
        minWidth: number;
      }[] = [
        {
          fieldKey: SEVEN_SEGMENTS_USL_LINE_FIELDS.COMPANY_CODE,
          minWidth: 120,
        },
        { fieldKey: SEVEN_SEGMENTS_USL_LINE_FIELDS.LOCATION, minWidth: 120 },
        { fieldKey: SEVEN_SEGMENTS_USL_LINE_FIELDS.COST_CENTER, minWidth: 120 },
        {
          fieldKey: SEVEN_SEGMENTS_USL_LINE_FIELDS.ACCOUNT_NUMBER,
          minWidth: 140,
        },
        {
          fieldKey: SEVEN_SEGMENTS_USL_LINE_FIELDS.PRODUCT_LINE,
          minWidth: 120,
        },
        { fieldKey: SEVEN_SEGMENTS_USL_LINE_FIELDS.CHANNEL, minWidth: 120 },
        { fieldKey: SEVEN_SEGMENTS_USL_LINE_FIELDS.PROJECT, minWidth: 120 },
      ];

      const sevenSegmentColumns = sevenSegmentFields.map(
        ({ fieldKey, minWidth }) => ({
          id: fieldKey,
          header: USL_LINE_KEY_TO_HEADER[fieldKey],
          minWidth,
          cell: (item: ItemType) => {
            const discrepancies = getUslLineDiscrepancies(
              useCaseName,
              item.index,
            );
            return (
              <UseCaseTableCell
                uslLine={item.uslLine}
                uslLineKey={fieldKey}
                useCaseName={useCaseName}
                uslLineIndex={item.index}
                discrepancy={discrepancies?.[fieldKey]}
                uslLineType={discrepancies?.uslLineType}
              />
            );
          },
        }),
      );

      // Simple columns without discrepancy support
      const simpleColumnIds: (keyof UslLine)[] = [
        "debitOrCredit",
        "glLineDescriptionSuffix",
        "subscriptionProductLine",
        "invoiceAccountingClass",
      ];

      const simpleColumns = simpleColumnIds.map((id) => ({
        id,
        header: USL_LINE_KEY_TO_HEADER[id],
        minWidth: 120,
        cell: (item: ItemType) => (
          <UseCaseTableCell
            uslLine={item.uslLine}
            uslLineKey={id}
            useCaseName={useCaseName}
            uslLineIndex={item.index}
          />
        ),
      }));

      const [
        debitCreditColumn,
        glLineDescriptionColumn,
        subscriptionProductLineColumn,
        invoiceAccountingClassColumn,
      ] = simpleColumns;

      const standardColumns = [accountNameColumn, ...sevenSegmentColumns];

      if (useCaseName === UseCaseName.INVOICE) {
        return [
          ...standardColumns,
          debitCreditColumn,
          invoiceAccountingClassColumn,
        ];
      }

      return [
        ...standardColumns,
        debitCreditColumn,
        glLineDescriptionColumn,
        subscriptionProductLineColumn,
      ];
    },
    [getUslLineDiscrepancies, coaDescriptions, isLoadingCoaDescriptions],
  );

  // Get use cases from selected ARD
  const useCases = displayedArd?.usecases || [];

  // Helper function to get display name for use case
  const getUseCaseDisplayName = (useCase: UseCase): string => {
    if (useCase.name === "OTHER" && useCase.altName) {
      return `${useCase.name} (${useCase.altName})`;
    }
    return useCase.name || "Unknown";
  };

  return (
    <SpaceBetween direction="vertical" size="l">
      {useCases.length === 0 ? (
        <Container>
          <SpaceBetween size="m">
            <div>No use cases found</div>
          </SpaceBetween>
        </Container>
      ) : (
        useCases.map((useCase: UseCase, index: number) => {
          const uslLines = useCase.uslLines || [];
          const useCaseName = getUseCaseDisplayName(useCase);

          // Transform USL lines to include index for discrepancy lookup
          const itemsWithIndex = uslLines.map((uslLine, idx) => ({
            uslLine,
            index: idx,
          }));

          return (
            <div
              key={`${useCaseName}-${index}`}
              id={getUseCaseSectionId(useCase.name, index)}
              style={{ scrollMarginTop: `${ANCHOR_SCROLL_MARGIN_TOP}px` }}
            >
              <Table
                stickyHeader
                stickyHeaderVerticalOffset={SPLIT_PANEL_STICKY_HEADER_OFFSET}
                columnDefinitions={createColumnDefinitions(
                  useCase.name || "Unknown",
                )}
                items={itemsWithIndex}
                sortingDisabled={false}
                empty={
                  <SpaceBetween size="m">
                    <div>No USL lines found for this use case</div>
                  </SpaceBetween>
                }
                header={
                  <Header variant="h3" description={useCase.altName}>
                    {useCaseName}
                  </Header>
                }
              />
            </div>
          );
        })
      )}
    </SpaceBetween>
  );
}
